import React from 'react'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'

import ExtendedInput from './ExtendedInput/ExtendedInput'
import FormElement from './FormElement'

const Select = ({
    form,
    field,
    options,
    placeholder,
    loadingPlaceholder,
    emptyPlaceholder,
    loading,
    validating,
    displayErrorsOnlyWhenDirty,
    optionNameKey = null,
    optionValueKey = null,
    inlineDescription,
    size,
    quickList,
    ...props
}) => {
    const { name, value } = field
    const { touched, errors, dirty } = form
    const error = (!displayErrorsOnlyWhenDirty || (displayErrorsOnlyWhenDirty && dirty)) && touched[name] && errors[name]

    const renderOption = (option, key) => {
        const value = optionValueKey ? option[optionValueKey] : option.value
        const name = optionNameKey ? option[optionNameKey] : option.name

        return (
            <option key={key} value={value} disabled={option.disabled}>
                {name}
            </option>
        )
    }

    const renderOptions = (options) => {
        return options.map((option, i) => renderOption(option, i))
    }

    const renderOptionGroup = (option, i) => {
        return (
            <optgroup key={i} label={option.label}>
                {renderOptions(option.options)}
            </optgroup>
        )
    }

    const displayLoadingPlaceholder = loading && !!loadingPlaceholder
    const displayPlaceholder = !value && !displayLoadingPlaceholder && !!placeholder
    const displayEmptyPlaceholder = !value && !displayLoadingPlaceholder && !!emptyPlaceholder && (!options || options.length <= 0)

    return (
        <FormElement
            elementClass="form-element--select"
            name={name}
            value={value}
            error={error}
            loading={loading}
            validating={validating}
            inlineDescription={inlineDescription}
            {...props}
        >
            {() => (
                <ExtendedInput
                    type="text"
                    size={size}
                >
                    <select {...field} {...props}>
                        {displayPlaceholder && (
                            <option value={field.value} disabled>{placeholder}</option>
                        )}
                        {displayLoadingPlaceholder && (
                            <option value={field.value} disabled>{loadingPlaceholder}</option>
                        )}
                        {displayEmptyPlaceholder && !value && (
                            <option value={field.value} disabled>{emptyPlaceholder}</option>
                        )}
                        {(quickList && quickList.length) && renderOptionGroup(quickList, quickList.label)}
                        {options.map((option, i) => {
                            if (option.label) {
                                return renderOptionGroup(option, i)
                            } else {
                                return renderOption(option, i)
                            }
                        })}
                    </select>
                </ExtendedInput>
            )}
        </FormElement>
    )
}

Select.propTypes = {
    quickList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.string,
            name: PropTypes.string,
        })),
    })),
}

function areEqual (prevProps, nextProps) {
    if (prevProps.loading !== nextProps.loading) return false
    if (prevProps.validating !== nextProps.validating) return false
    if (prevProps.field.value !== nextProps.field.value) return false
    if (!isEqual(prevProps.meta, prevProps.meta)) return false
    if (!isEqual(prevProps.form.errors, nextProps.form.errors)) return false
    if (!isEqual(prevProps.form.submitCount, nextProps.form.submitCount)) return false
    return isEqual(prevProps.options, nextProps.options)
}

export default React.memo(Select, areEqual)
